import React, { createContext, useState, useContext, useCallback } from 'react';
import { Flashbar, FlashbarProps } from "@cloudscape-design/components";
import { v4 as uuidv4 } from 'uuid';
import {NotificationType} from '../types/types';

interface NotificationContextType {
  addNotification: (type: NotificationType, message: string) => void;
  removeNotification: (id: string) => void;
  addLoaderNotification: (id: string) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<FlashbarProps.MessageDefinition[]>([]);

  const addNotification = useCallback((type: NotificationType, message: string) => {
    const id = uuidv4();
    const notification: FlashbarProps.MessageDefinition = {
      type,
      content: message,
      dismissible: true,
      id,
      onDismiss: () => removeNotification(id)
    };

    setNotifications(prev => [...prev, notification]);

    // Auto-dismiss after 15 seconds
    setTimeout(() => removeNotification(id), 15000);
  }, []);

  const removeNotification = useCallback((id: string) => {
    if(id === "all"){
      setNotifications([]);
    }else{
      setNotifications(prev => prev.filter(notification => notification.id !== id));
    }
  }, []);

  const addLoaderNotification = useCallback((message: string)=>{
    const id = uuidv4();
    const notification: FlashbarProps.MessageDefinition = {
      type: "in-progress",
      loading: true,
      content: message,
      dismissible: false,
      id,
    };

    setNotifications([notification]);

  },[])

  return (
    <NotificationContext.Provider value={{ addNotification, removeNotification, addLoaderNotification }}>
      {children}
      <div className='flashbar-container'>
        <Flashbar
          items={notifications}
          stackItems={true}
        />
      </div>
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};